import {FormikProvider, useFormik} from 'formik';
import {forEach, isEqual, merge} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, ClientInfoControls, Form} from 'components';
import {AccountAssignmentInfo, InviteeParticipant, UserInfo} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, InviteeParticipantRole} from 'types';
import {userInfoUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.UPDATE_PARTICIPANT_INFO_MODAL;

type FormFields = {
    userInfo: UserInfo;
};

type Props = {
    open: boolean;
    loading: boolean;
    assignment: AccountAssignmentInfo;
    participant: InviteeParticipant;
    basePath: string;
    error?: ApiError;
    guestEnabled?: boolean;
    onClose: (confirm: boolean, payload: {updates: {userInfo: UserInfo}}) => void;
};

// rename to invitee
export const ClientDataModal: React.FC<Props> = ({
    open, loading, participant, basePath, guestEnabled, assignment, error, onClose
}: Props) => {
    const {t} = useTranslation();

    const currentUserInfo = useMemo(() => participant?.data?.userInfo, [participant?.data?.userInfo]);

    const formik = useFormik({
        initialValues: {
            userInfo: merge(userInfoUtils.generateEmpty(), participant?.data?.userInfo)
        },
        onSubmit: (formValues: FormFields) => {
            const payload = {
                updates: {
                    userInfo: userInfoUtils.format(formValues.userInfo)
                },
                participantId: participant.id
            };
            onClose(true, payload);
        }
    });
    const {submitForm, values, setFieldError} = formik;

    const dirty = useMemo(() => !isEqual(currentUserInfo, values), [currentUserInfo, values]);

    const formErrors = useMemo(() => userInfoUtils.getUserInfoErrors(error, t), [error, t]);

    const onModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            submitForm();
        } else {
            onClose(false, null);
        }
    }, [submitForm, onClose]);

    useEffect(() => {
        forEach(formErrors, (value, key) => value && setFieldError(key, value));
    }, [formErrors, setFieldError]);

    return (
        <ConfirmModal
            open={open}
            title={t(T_KEYS.TITLE)}
            okDisabled={loading || !dirty}
            onClose={onModalClose}
        >
            <FormikProvider value={formik}>
                <Form>
                    <ClientInfoControls
                        assignment={assignment}
                        basePath={basePath}
                        hideTitle
                        guestEnabled={guestEnabled}
                        role={participant?.role as unknown as InviteeParticipantRole}
                    />
                </Form>
            </FormikProvider>
        </ConfirmModal>
    );
};